import React, { type ReactNode } from "react";
import { type LinkProps, Flex, Text } from "@chakra-ui/react";
import { LinkCard } from "ui/link-card";
import { ArrowLink } from "ui/link";
import formatDate from "date-fns/format";
import Balancer from "react-wrap-balancer";

type FeaturedResourceCardProps = {
  href: LinkProps["href"];
  linkAriaLabel?: string;
  actionLabel?: string;
  publishedAt?: string;
  title?: string;
  excerpt?: string;
  image: ReactNode;
};

export function FeaturedResourceCard({
  image,

  href,
  linkAriaLabel,
  title,
  excerpt,
  actionLabel,
  publishedAt,
}: FeaturedResourceCardProps) {
  return (
    <LinkCard
      href={href}
      linkAriaLabel={linkAriaLabel}
      border="1px solid"
      borderColor="base.border"
      borderRadius="base"
      p={0}
      overflow="hidden" // Hide image border overflow
      transition=".15s all ease-in-out"
      _hover={{ borderColor: "gray.400" }}
    >
      <Flex direction={{ base: "column", md: "row" }} alignItems="stretch" w="100%">
        <Flex flex={1}>{image}</Flex>

        <Flex
          flex={{ base: 1, md: 0.8 }}
          direction="column"
          gap={3}
          px={6}
          py={4}
          borderTop={{ base: "1px solid", md: "none" }}
          borderTopColor={{ base: "base.border", md: "transparent" }}
          borderLeft={{ base: "none", md: "1px solid" }}
          borderLeftColor={{ base: "transparent", md: "base.border" }}
        >
          {title && (
            <Text as="h1" textStyle="h7" fontWeight="semibold" transition="150ms all" _groupHover={{ color: "grass" }}>
              <Balancer> {title}</Balancer>
            </Text>
          )}

          {excerpt && <Text fontSize="md">{excerpt}</Text>}

          {(actionLabel ?? publishedAt) && (
            <Flex align="center" justify="space-between" mt="auto" width="100%">
              {publishedAt && (
                <Text fontSize="sm" color="text.secondary">
                  {formatDate(new Date(publishedAt), "MMMM d, yyyy")}
                </Text>
              )}
              {actionLabel && (
                <ArrowLink size="md" variant="grass" mt={2}>
                  {actionLabel}
                </ArrowLink>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </LinkCard>
  );
}
